import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { useT } from '../i18n/index.js';
import type { TourDef, TourPlacement } from './tours.js';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const BUBBLE_WIDTH = 320;
const GAP = 12;
const RING_PAD = 6;

/** Égalité de positions (au pixel près) pour éviter de re-rendre quand rien n'a bougé. */
function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.round(a.top) === Math.round(b.top) &&
    Math.round(a.left) === Math.round(b.left) &&
    Math.round(a.width) === Math.round(b.width) &&
    Math.round(a.height) === Math.round(b.height)
  );
}

/**
 * Position d'un élément cible **visible** à l'écran, ou `null` sinon (non monté, masqué, ou hors
 * champ). On cherche d'abord un attribut `data-tour` (rail, éléments dédiés), puis on retombe sur
 * `#id` : beaucoup de sections de page portent déjà un `id` d'ancre (nav), qu'on réutilise sans les
 * modifier.
 *
 * Le test de visibilité règle le cas **mobile** : sous `lg`, le rail latéral est replié hors écran
 * (translaté), donc les entrées de nav existent mais ne sont pas visibles — on ne veut pas y ancrer
 * un anneau « dans le vide ». `null` fait retomber la bulle en position centrée, sans anneau.
 */
function targetRect(target: string): Rect | null {
  const el =
    document.querySelector<HTMLElement>(`[data-tour="${target}"]`) ??
    document.getElementById(target);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null;
  // Centre de l'élément hors du cadre visible (rail replié sur mobile, section défilée hors vue) :
  // on considère la cible non ancrable.
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  if (cx < 0 || cx > window.innerWidth || cy < 0 || cy > window.innerHeight) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/** Place la bulle près de la cible selon le côté préféré, bornée à la fenêtre. */
function bubblePosition(
  rect: Rect,
  placement: TourPlacement,
  bubbleH: number,
): { top: number; left: number } {
  let top: number;
  let left: number;
  switch (placement) {
    case 'right':
      left = rect.left + rect.width + GAP;
      top = rect.top;
      break;
    case 'left':
      left = rect.left - BUBBLE_WIDTH - GAP;
      top = rect.top;
      break;
    case 'top':
      left = rect.left;
      top = rect.top - bubbleH - GAP;
      break;
    default:
      left = rect.left;
      top = rect.top + rect.height + GAP;
  }
  left = Math.max(GAP, Math.min(left, window.innerWidth - BUBBLE_WIDTH - GAP));
  top = Math.max(GAP, Math.min(top, window.innerHeight - bubbleH - GAP));
  return { top, left };
}

/**
 * Moteur de visite guidée (ADR-0031). Maison plutôt qu'une bibliothèque tierce : plus léger que le
 * budget de bundle (NFR-11) et compatible avec la CSP. Rend un fond assombri, un anneau « spotlight »
 * sur l'élément ancre, et une bulle positionnée à côté.
 *
 * Accessible (NFR-22) : `role="dialog"` + `aria-modal`, contenu annoncé (`aria-live`), fermeture par
 * Échap, navigation ←/→, focus déplacé sur la bulle et **piégé** dedans le temps de la visite, focus
 * restauré à la fermeture. « Passer » est toujours atteignable — la visite n'emprisonne jamais.
 */
export function GuidedTour({
  tour,
  onClose,
}: {
  tour: TourDef;
  /** Fin de la visite : `skipped` distingue « passée » de « terminée ». */
  onClose: (skipped: boolean) => void;
}) {
  const t = useT();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const nextBtnRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const step = tour.steps[index];
  const total = tour.steps.length;
  const isLast = index === total - 1;

  // Mémorise l'élément focalisé avant la visite pour lui rendre le focus à la fermeture.
  useEffect(() => {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    return () => restoreFocusRef.current?.focus?.();
  }, []);

  // Recalcule la position de la cible à chaque étape, et sur redimensionnement/défilement.
  const measure = useCallback(() => {
    setRect(step ? targetRect(step.target) : null);
  }, [step]);

  useLayoutEffect(() => {
    // On re-mesure sur ~30 frames (~0,5 s) : le temps qu'une cible monte après la navigation, et
    // surtout que le tiroir de nav (mobile) finisse de coulisser — l'anneau **suit** alors la cible
    // en mouvement au lieu de se figer à mi-parcours. `setRect` n'écrit que si la position change.
    let frame = 0;
    let raf = 0;
    const tick = () => {
      const next = step ? targetRect(step.target) : null;
      setRect((prev) => (sameRect(prev, next) ? prev : next));
      if (frame < 30) {
        frame += 1;
        raf = requestAnimationFrame(tick);
      }
    };
    tick();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure, step]);

  // Positionne la bulle une fois sa hauteur connue, puis y place le focus.
  useLayoutEffect(() => {
    if (rect && bubbleRef.current) {
      setPos(bubblePosition(rect, step?.placement ?? 'bottom', bubbleRef.current.offsetHeight));
    } else if (!rect) {
      // Cible absente : bulle centrée, sans anneau (la visite reste utilisable).
      setPos(null);
    }
  }, [rect, step?.placement, index]);

  useEffect(() => {
    nextBtnRef.current?.focus();
  }, [index]);

  const next = useCallback(() => {
    if (isLast) onClose(false);
    else setIndex((i) => i + 1);
  }, [isLast, onClose]);
  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  // Clavier : Échap passe, ←/→ naviguent, Tab reste piégé dans la bulle.
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose(true);
    } else if (e.key === 'ArrowRight') {
      next();
    } else if (e.key === 'ArrowLeft') {
      prev();
    } else if (e.key === 'Tab') {
      const focusables = bubbleRef.current?.querySelectorAll<HTMLElement>('button');
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // Étape hors bornes (ne devrait pas arriver) : on n'affiche rien plutôt que de planter.
  if (!step) return null;

  return (
    <div className="ddp-tour" onKeyDown={onKeyDown}>
      {/* Fond modal : capte les clics pour que la visite reste au premier plan. */}
      <div className="ddp-tour__backdrop" />
      {rect && (
        <div
          className="ddp-tour__ring"
          style={{
            top: rect.top - RING_PAD,
            left: rect.left - RING_PAD,
            width: rect.width + RING_PAD * 2,
            height: rect.height + RING_PAD * 2,
          }}
        />
      )}
      <div
        ref={bubbleRef}
        className={`ddp-tour__bubble${pos ? '' : ' ddp-tour__bubble--centered'}`}
        style={pos ? { top: pos.top, left: pos.left } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={t(step.titleKey)}
      >
        <div className="ddp-tour__body" aria-live="polite">
          <p className="ddp-tour__step">
            {t('tour.step')} {index + 1}/{total}
          </p>
          <h2 className="ddp-tour__title">{t(step.titleKey)}</h2>
          <p className="ddp-tour__text">{t(step.textKey)}</p>
        </div>
        <div className="ddp-tour__foot">
          <span className="ddp-tour__dots" aria-hidden="true">
            {tour.steps.map((s, k) => (
              <i key={s.target} className={k === index ? 'on' : ''} />
            ))}
          </span>
          <button type="button" className="ddp-tour__skip" onClick={() => onClose(true)}>
            {t('tour.skip')}
          </button>
          {index > 0 && (
            <button type="button" className="ddp-tour__prev" onClick={prev}>
              {t('tour.prev')}
            </button>
          )}
          <button type="button" className="ddp-tour__next" ref={nextBtnRef} onClick={next}>
            {isLast ? t('tour.finish') : t('tour.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
